import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';

import Layout from '../../components/layout/AppLayout';
import TransferLendTabs from '../../components/inventory/TransferLendTabs';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import { SegmentedControl, StatusPill } from '../../../shared/components/ui/primitives';
import { getLends } from '../../services/api/implementation/impl';
import {
  getSavedInventoryProjectId,
  saveInventoryProjectId
} from '../../hooks/useInventorySelection';
import { ConvertLendModal, ReturnLendModal } from '../../components/inventory/LendModals';
import {
  errorMessage,
  formatDate,
  formatDateTime,
  roundQty,
  transferStatus
} from '../../utils/stockTransfers';

const PAGE_SIZE = 20;
const formatRate = (rate) =>
  rate == null ? '—' : `₹${Number(rate).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** "Project · Warehouse" on two lines. */
const Place = ({ project, warehouse }) => (
  <div>
    <p className="text-gray-900">{warehouse || '—'}</p>
    <p className="text-xs text-gray-500">{project || ''}</p>
  </div>
);

/**
 * W3.2: stock lent to or borrowed from the company's other projects.
 * The borrower returns it (all or in parts); the lender can let them keep it.
 */
const Lends = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useState(
    () => Number(location.state?.projectID) || getSavedInventoryProjectId() || ''
  );
  const [direction, setDirection] = useState(
    location.state?.direction === 'borrowed' ? 'borrowed' : 'lent_out'
  );
  const [status, setStatus] = useState('open');
  const [overdueOnly, setOverdueOnly] = useState(location.state?.overdue === true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [returning, setReturning] = useState(null); // lend being returned
  const [converting, setConverting] = useState(null); // lend being converted
  const [focusId, setFocusId] = useState(null); // lend a notification opened

  // A notification opens one lend: its project, side and list, then the row.
  // An effect rather than initial state so it also works when this page is
  // already open and the bell is clicked.
  useEffect(() => {
    const linkedProject = Number(searchParams.get('projectID'));
    if (!linkedProject) return;
    setProjectId(linkedProject);
    const linkedDirection = searchParams.get('direction');
    if (linkedDirection === 'borrowed' || linkedDirection === 'lent_out') {
      setDirection(linkedDirection);
    }
    const linkedStatus = searchParams.get('status');
    setStatus(['open', 'closed', 'all'].includes(linkedStatus) ? linkedStatus : 'open');
    setOverdueOnly(searchParams.get('overdue') === '1');
    setPage(1);
    setFocusId(Number(searchParams.get('id')) || null);
  }, [searchParams]);

  useEffect(() => {
    if (!projects?.length) return;
    if (!projectId || !projects.some((p) => Number(p.id) === Number(projectId))) {
      setProjectId(getSavedInventoryProjectId(projects) || projects[0].id);
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (projectId) saveInventoryProjectId(projectId);
    setPage(1);
    setExpanded(null);
  }, [projectId]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['lends', projectId, direction, status, overdueOnly, page],
    queryFn: () =>
      getLends({
        project_id: projectId,
        direction,
        status,
        ...(overdueOnly && { overdue: true }),
        page,
        limit: PAGE_SIZE
      }),
    enabled: !!projectId
  });
  const lends = data?.items || [];
  const counts = data?.counts || null;
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const borrowedTab = direction === 'borrowed';

  useEffect(() => {
    const match = focusId && lends.find((lend) => Number(lend.id) === focusId);
    if (match) {
      setExpanded(match.id);
      setFocusId(null);
    }
  }, [focusId, lends]);

  const resetList = () => {
    setPage(1);
    setExpanded(null);
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['lends'] });
    queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    queryClient.invalidateQueries({ queryKey: ['transfer-from-stock'] });
    queryClient.invalidateQueries({ queryKey: ['AllInventories'] });
    queryClient.invalidateQueries({ queryKey: ['inventoryByItem'] });
    queryClient.invalidateQueries({ queryKey: ['inventoryMovements'] });
  };

  const tabLabel = (label, open, overdue) =>
    counts ? `${label} (${open || 0})${Number(overdue) > 0 ? ` · ${overdue} overdue` : ''}` : label;

  const selectClass =
    'h-10 min-w-[12rem] max-w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800';

  return (
    <Layout>
      <Loader isVisible={isProjectsLoading || (isFetching && !data)} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Transfers & Lends' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="px-4 pb-8 pt-[5.5rem] sm:px-6">
        <TransferLendTabs active="/builder/inventory/lends" projectId={projectId} />
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">Stock lent and borrowed</h2>
            <p className="text-sm text-gray-500">
              Stock lent to the company&apos;s other projects must come back, unless the lender lets
              them keep it.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() =>
                navigate('/builder/inventory/transfers', {
                  state: { projectID: Number(projectId), scope: 'other', kind: 'lend', openForm: true }
                })
              }
              className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
              Lend stock
            </button>
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(Number(event.target.value))}
              className={selectClass}>
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SegmentedControl
            ariaLabel="Lent out or borrowed"
            className="max-w-full overflow-x-auto"
            value={direction}
            onChange={(value) => {
              setDirection(value);
              resetList();
            }}
            options={[
              {
                value: 'lent_out',
                label: tabLabel('Lent out', counts?.lent_out, counts?.lent_out_overdue)
              },
              {
                value: 'borrowed',
                label: tabLabel('Borrowed', counts?.borrowed, counts?.borrowed_overdue)
              }
            ]}
          />
          <SegmentedControl
            size="sm"
            tone="neutral"
            ariaLabel="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              if (value === 'closed') setOverdueOnly(false);
              resetList();
            }}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All' }
            ]}
          />
          {status !== 'closed' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={overdueOnly}
                onChange={(e) => {
                  setOverdueOnly(e.target.checked);
                  resetList();
                }}
              />
              Overdue only
            </label>
          )}
        </div>

        {isError && (
          <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage(error, 'Could not load lends')}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                <tr>
                  <th className="w-8 px-4 py-3" />
                  <th className="px-4 py-3">Lend</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Lines</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {lends.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                      {isFetching
                        ? 'Loading…'
                        : borrowedTab
                          ? 'Nothing borrowed here'
                          : 'Nothing lent out here'}
                    </td>
                  </tr>
                ) : (
                  lends.map((lend) => {
                    const open = expanded === lend.id;
                    const isOpen = lend.status === 'open';
                    const pill = transferStatus(lend.status);
                    const items = lend.items || [];
                    return (
                      <React.Fragment key={lend.id}>
                        <tr
                          className="cursor-pointer border-t border-gray-100 align-top hover:bg-gray-50"
                          onClick={() => setExpanded(open ? null : lend.id)}>
                          <td className="px-4 py-3 text-gray-500">
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">#{lend.id}</p>
                            <p className="whitespace-nowrap text-xs text-gray-500">
                              {formatDate(lend.created_at)}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <Place
                              project={lend.from_project_name}
                              warehouse={lend.from_warehouse?.name}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Place
                              project={lend.to_project_name}
                              warehouse={lend.to_warehouse?.name}
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {lend.due_date ? formatDate(lend.due_date) : 'No due date'}
                            {lend.overdue && (
                              <span className="ml-2 inline-block">
                                <StatusPill dense tone="danger">
                                  Overdue
                                </StatusPill>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <p>{items.length} item(s)</p>
                            {isOpen && (
                              <p className="text-xs text-gray-500">
                                {lend.outstanding_lines ?? 0} still out
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill dense tone={pill.tone}>
                              {pill.label}
                            </StatusPill>
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {isOpen && borrowedTab && (
                              <button
                                type="button"
                                onClick={() => setReturning(lend)}
                                className="h-8 rounded-lg bg-[#2A565E] px-3 text-xs font-semibold text-white">
                                Return
                              </button>
                            )}
                            {isOpen && !borrowedTab && (
                              <button
                                type="button"
                                onClick={() => setConverting(lend)}
                                className="h-8 whitespace-nowrap rounded-lg border border-[#2A565E] px-3 text-xs font-semibold text-[#2A565E]">
                                Convert to transfer
                              </button>
                            )}
                            {!isOpen && <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50">
                            <td />
                            <td colSpan={7} className="px-4 py-3">
                              <div className="overflow-x-auto">
                                <table className="min-w-[30rem] text-left text-xs">
                                  <thead className="text-gray-500">
                                    <tr>
                                      <th className="py-1 pr-6">Item</th>
                                      <th className="py-1 pr-6">Lent</th>
                                      <th className="py-1 pr-6">Returned</th>
                                      <th className="py-1 pr-6">Outstanding</th>
                                      <th className="py-1 pr-6">Rate</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item) => (
                                      <tr key={item.id}>
                                        <td className="py-1 pr-6 text-gray-800">
                                          {item.item_name || `Line #${item.id}`}
                                        </td>
                                        <td className="py-1 pr-6">
                                          {roundQty(item.quantity)} {item.unit || ''}
                                        </td>
                                        <td className="py-1 pr-6">{roundQty(item.qty_returned)}</td>
                                        <td
                                          className={`py-1 pr-6 ${
                                            Number(item.outstanding) > 0
                                              ? 'font-semibold text-amber-700'
                                              : ''
                                          }`}>
                                          {roundQty(item.outstanding)}
                                        </td>
                                        <td className="py-1 pr-6">{formatRate(item.rate)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                                <span>Lent on {formatDateTime(lend.created_at)}</span>
                                {lend.status === 'returned' && lend.closed_at && (
                                  <span>Fully returned on {formatDateTime(lend.closed_at)}</span>
                                )}
                                {lend.converted_at && (
                                  <span>Converted on {formatDateTime(lend.converted_at)}</span>
                                )}
                                {lend.note && <span>Note: {lend.note}</span>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {total > PAGE_SIZE && (
          <div className="mt-2">
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(Math.min(Math.max(1, p), totalPages))}
              isFetching={isFetching}
            />
          </div>
        )}
      </div>

      {returning && (
        <ReturnLendModal
          lend={returning}
          projectId={projectId}
          onClose={() => setReturning(null)}
          onDone={refresh}
        />
      )}
      {converting && (
        <ConvertLendModal lend={converting} onClose={() => setConverting(null)} onDone={refresh} />
      )}
    </Layout>
  );
};

export default Lends;
